import  { createContext, useContext, useReducer, useEffect } from "react";
import { fetchAppData } from "../Utils/FirebaseHelpers";
import { useUserEmail } from "../../../../context/UserEmailContext";

const CfmContext = createContext();
const persisted = JSON.parse(localStorage.getItem("cfmContextData"));
const defaultEmptyState = {
  details: [],
  pensions: [],
  investments: [],
  income: [],
  regularIncome: [],
  drawdowns: [],
  dropRiskData: [],
  adHocData: [],
  inflation: "2.5", // Default inflation rate
  calculatedIncome: [],
  pensionResults: [],
  investmentResults: [],
  incomeExpenditureResults: [],
  graphNotes: {},
  docId: null, // important for checking later
};

const initialState = persisted?.docId ? persisted : defaultEmptyState;


const reducer = (state, action) => {
  switch (action.type) {
    case "RESET_CONTEXT":
      return defaultEmptyState;

    case "LOAD_ALL":
      return { ...state, ...action.payload };

    case "SET_DOC_ID":
      return { ...state, docId: action.payload };

    case "SET_PENSIONS":
      return { ...state, pensions: action.payload };

    case "UPDATE_INCOME":
      return { ...state, income: action.payload };

    case "UPDATE_REGULARINCOME":
      return { ...state, regularIncome: action.payload };

    case "UPDATE_DETAILS": {
      const updatedDetails = action.payload;

      const oldNames = state.details.map((d) => d.name?.trim());
      const newNames = updatedDetails
        .map((d) => d.name?.trim())
        .filter(Boolean);
      const validNames = newNames; // full trimmed names

      // Create a map from firstName => updated full name
      const nameMap = oldNames.reduce((acc, oldName, index) => {
        const newName = updatedDetails[index]?.name?.trim();
        const firstOld = oldName?.split(" ")[0];
        const firstNew = newName?.split(" ")[0];

        if (firstOld && firstNew && firstOld !== firstNew) {
          acc[firstOld] = firstNew;
        }
        return acc;
      }, {});

      const validFirstNames = newNames.map((n) => n.split(" ")[0]);

      const updateNameRefs = (items) =>
        Array.isArray(items)
          ? items
              .filter((item) => {
                const base = item.name?.trim();
                return validNames.includes(base) || nameMap[base];

                // return validFirstNames.includes(base) || nameMap[base];
              })
              .map((item) => {
                const newName = nameMap[item.name?.trim()];
                return newName
                  ? { ...item, name: newName.split(" ")[0] }
                  : item;
              })
          : [];

      return {
        ...state,
        details: updatedDetails,
        pensions: updateNameRefs(state.pensions),
        investments: updateNameRefs(state.investments),
        income: updateNameRefs(state.income),
        regularIncome: updateNameRefs(state.regularIncome),
        phasedData: updateNameRefs(state.phasedData),
        adHocData: updateNameRefs(state.adHocData),
        // dropRiskData: updateNameRefs(state.dropRiskData),
        drawdowns: updateNameRefs(state.drawdowns), //  FIXED HERE
        calculatedIncome: "0", // reset calculated income when details change
      };
    }

    case "UPDATE_DRAWDOWNS":
      return { ...state, drawdowns: action.payload };

    case "UPDATE_CONTEXT_FIELD": {
      const { field, index, key, value } = action.payload;
      const items = [...state[field]];
      if (!items[index]) return state;

      items[index] = { ...items[index], [key]: value };
      return { ...state, [field]: items };
    }

    case "UPDATE_PENSIONS": {
      const updatedPensions = action.payload;

      const allInvestmentKeys = (state.investments || []).map(
        (inv) => `${inv.name?.trim()} - ${inv.type?.trim()}`
      );

      const allPensionKeys = updatedPensions.map(
        (p) => `${p.name?.trim()} - ${p.type?.trim()}`
      );

      const validKeys = [...allPensionKeys, ...allInvestmentKeys];

      const filterByValidSource = (list) =>
        (list || []).filter((item) =>
          validKeys.includes(`${item.name?.trim()} - ${item.type?.trim()}`)
        );

      return {
        ...state,
        pensions: updatedPensions,
        regularIncome: filterByValidSource(state.regularIncome),
        drawdowns: filterByValidSource(state.drawdowns),
        tfcData: filterByValidSource(state.tfcData),
        phasedData: filterByValidSource(state.phasedData),
        adHocData: filterByValidSource(state.adHocData),
        // dropRiskData: filterByValidSource(state.dropRiskData),
      };
    }

    case "UPDATE_INVESTMENTS": {
      const updatedInvestments = action.payload;

      const allPensionKeys = (state.pensions || []).map(
        (p) => `${p.name?.trim()} - ${p.type?.trim()}`
      );

      const allInvestmentKeys = updatedInvestments.map(
        (inv) => `${inv.name?.trim()} - ${inv.type?.trim()}`
      );

      const validKeys = [...allPensionKeys, ...allInvestmentKeys];

      const filterByValidSource = (list) =>
        (list || []).filter((item) =>
          validKeys.includes(`${item.name?.trim()} - ${item.type?.trim()}`)
        );

      return {
        ...state,
        investments: updatedInvestments,
        regularIncome: filterByValidSource(state.regularIncome),
        drawdowns: filterByValidSource(state.drawdowns),
        tfcData: filterByValidSource(state.tfcData),
        phasedData: filterByValidSource(state.phasedData),
        adHocData: filterByValidSource(state.adHocData),
        // dropRiskData: filterByValidSource(state.dropRiskData),
      };
    }

    case "UPDATE_REGULAR_INCOME":
      return {
        ...state,
        regularIncome: action.payload,
      };

    case "SET_ADHOC_DATA":
      return {
        ...state,
        adHocData: action.payload,
      };

    case "SET_DROPRISK_DATA":
      return {
        ...state,
        dropRiskData: action.payload,
      };

    case "UPDATE_INFLATION":
      return { ...state, inflation: action.payload };

    // case "SET_CALCULATED_INCOME":
    //   return {
    //     ...state,
    //     calculatedIncome: action.payload ? action.payload : "0",
    //   };
    case "SET_PENSION_RESULTS":
      return {
        ...state,
        pensionResults: action.payload,
        incomeExpenditureResults: action.payload,
        investmentResults: action.payload,
      };

    case "SET_INVESTMENT_RESULTS":
      return { ...state, investmentResults: action.payload };

    case "SET_INCOME_EXPENDITURE_RESULTS":
      return { ...state, incomeExpenditureResults: action.payload };

    case "SET_CALCULATED_INCOME":
      return {
        ...state,
        calculatedIncome: action.payload,
      };

    case "UPDATE_INCOME_EXPENDITURE_CHART_IMAGE":
      return {
        ...state,
        incomeExpenditureChartImages: {
          ...state.incomeExpenditureChartImages,
          [action.payload.name]: action.payload.image,
        },
      };

    case "UPDATE_SURPLUS_DEFICIT_CHART_IMAGE":
      return {
        ...state,
        surpluDeficitChartImages: {
          ...state.surpluDeficitChartImages,
          [action.payload.name]: action.payload.image,
        },
      };

    case "UPDATE_PENSION_CHART_IMAGE":
      return {
        ...state,
        pensionChartImages: {
          ...state.pensionChartImages,
          [action.payload.name]: action.payload.image,
        },
      };

    case "UPDATE_INVESTMENT_CHART_IMAGE":
      return {
        ...state,
        investmentChartImages: {
          ...state.investmentChartImages,
          [action.payload.name]: action.payload.image,
        },
      };

    case "UPDATE_COMBINED_INC_CHART":
      return {
        ...state,
        combinedIncExpChartImages: {
          ...state.combinedIncExpChartImages,
          [action.payload.name]: action.payload.image,
        },
      };

    case "UPDATE_COMBINED_SUR_DEF_CHART":
      return {
        ...state,
        combinedSurDefChartImages: {
          ...state.combinedSurDefChartImages,
          [action.payload.name]: action.payload.image,
        },
      };
    case "UPDATE_STACKED_INV_CHART":
      return {
        ...state,
        stackedInvChartImages: {
          ...state.stackedInvChartImages,
          [action.payload.name]: action.payload.image,
        },
      };
    case "UPDATE_STACKED_PEN_CHART":
      return {
        ...state,
        stackedPenChartImages: {
          ...state.stackedPenChartImages,
          [action.payload.name]: action.payload.image,
        },
      };

    case "UPDATE_NOTES":
      return {
        ...state,
        notes: action.payload,
      };

    case "UPDATE_GRAPH_NOTES": {
      const { person, tab, value } = action.payload;
      const updatedNotes = {
        ...state.graphNotes,
        [person]: {
          ...(state.graphNotes?.[person] || {}),
          [tab]: value,
        },
      };

      return {
        ...state,
        graphNotes: updatedNotes,
      };
    }

    case "SET_SCENARIOS":
      return { ...state, scenarios: action.payload };

    case "ADD_SCENARIO":
      return {
        ...state,
        scenarios: [...(state.scenarios || []), action.payload],
      };

    default:
      return state;
  }
};

export const CfmProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { userEmail } = useUserEmail(); //  Get user ID/email here

  useEffect(() => {
    // console.log("Global context updated:", state);
    localStorage.setItem("cfmContextData", JSON.stringify(state));
    // const total = calculateAllClientResults(state);
    // dispatch({ type: "SET_CALCULATED_INCOME", payload: total });
  }, [state]);

  const saveDataToFirebase = async () => {
    if (!userEmail) return;
    // await saveAppData(userEmail, state);
  };

  const loadDataFromFirebase = async () => {
    if (!userEmail) return;
    const data = await fetchAppData(userEmail);
    if (data) dispatch({ type: "LOAD_ALL", payload: data });
  };

  return (
    <CfmContext.Provider
      value={{ state, dispatch, saveDataToFirebase, loadDataFromFirebase }}
    >
      {children}
    </CfmContext.Provider>
  );
};

export const useCfmContext = () => useContext(CfmContext);
